/**
 * Tiện ích hỗ trợ tính toán độ phân giải thích ứng cho Điện thoại (Portrait/Landscape 720p)
 * và vẽ các Modal tương tác AI (ImageModal, VideoModal, FacilityDemoOverlay, RecommendationPopup)
 * lên Canvas 2D khi ghi hình trên trình duyệt di động.
 */

export interface OptimalRecordingDimensions {
    width: number;
    height: number;
    fps: number;
    videoBitsPerSecond: number;
    isMobile: boolean;
}

export function isMobileRecordingViewport(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
    const isSmallTouchViewport = window.innerWidth <= 900 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    return isMobileUA || isSmallTouchViewport;
}

/**
 * Tự động chọn khung hình tối ưu:
 * - Trên Điện thoại cầm dọc (Portrait): 720x1280 (9:16) @ 24fps, 2.5 Mbps -> Không bị phóng to cắt mất 75% màn hình & Avatar, chống tràn RAM iOS.
 * - Trên Điện thoại xoay ngang (Landscape): 1280x720 (16:9) @ 24fps, 2.5 Mbps.
 * - Trên Máy tính (PC/Laptop): Giữ nguyên 1920x1080 (hoặc cấu hình truyền vào) @ 30fps, 4.5 Mbps.
 */
export function resolveOptimalRecordingDimensions(
    requestedW?: number,
    requestedH?: number,
    requestedFps?: number,
    requestedBitrate?: number
): OptimalRecordingDimensions {
    const isMobile = isMobileRecordingViewport();
    if (!isMobile) {
        return {
            width: requestedW || 1920,
            height: requestedH || 1080,
            fps: requestedFps || 30,
            videoBitsPerSecond: requestedBitrate || 4_500_000,
            isMobile: false
        };
    }

    const isPortrait = typeof window !== 'undefined' && window.innerHeight > window.innerWidth;
    return {
        width: isPortrait ? 720 : 1280,
        height: isPortrait ? 1280 : 720,
        fps: Math.min(requestedFps || 24, 24),
        videoBitsPerSecond: Math.min(requestedBitrate || 2_500_000, 2_500_000),
        isMobile: true
    };
}

/**
 * Vẽ các Modal tài liệu hoặc Popup tương tác AI đang mở toàn màn hình lên Canvas ghi hình
 * (dành cho chế độ Mobile Canvas Compositor khi không có getDisplayMedia).
 */
export function drawActiveInteractiveModals(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    isCleanSourceFn: (source: CanvasImageSource) => boolean,
    getCorsSafeImageFn: (url: string) => HTMLImageElement | null
): void {
    if (typeof document === 'undefined') return;

    try {
        // 1. Kiểm tra các thẻ ảnh lớn hoặc video đang mở trong Modal tài liệu (ImageModal / VideoModal / FacilityDemoOverlay)
        const modalCandidates = document.querySelectorAll<HTMLElement>(
            '[role="dialog"]:not([data-testid="web-recording-mic-modal"]), .fixed.inset-0.z-50, .fixed.inset-0.z-\\[50\\], .fixed.inset-0.z-\\[100\\], .fixed.inset-0.z-\\[9999\\]'
        );

        for (let i = 0; i < modalCandidates.length; i++) {
            const modalEl = modalCandidates[i];
            if (!modalEl.isConnected) continue;
            const style = window.getComputedStyle(modalEl);
            if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity || '1') <= 0.05) {
                continue;
            }
            const rect = modalEl.getBoundingClientRect();
            if (rect.width < 120 || rect.height < 120) continue;

            // Tìm media chính bên trong modal (video đang phát hoặc ảnh tài liệu)
            const videoEl = modalEl.querySelector<HTMLVideoElement>('video');
            const imgEl = modalEl.querySelector<HTMLImageElement>('img:not([src*="icon"]):not([class*="w-4"]):not([class*="w-5"])');

            const hasValidVideo = Boolean(videoEl && videoEl.readyState >= 2 && videoEl.videoWidth > 0 && videoEl.videoHeight > 0);
            const hasValidImg = Boolean(imgEl && imgEl.complete && imgEl.naturalWidth > 120 && imgEl.naturalHeight > 120);

            if (!hasValidVideo && !hasValidImg) continue;

            ctx.save();
            // Phủ lớp nền kính mờ tối để làm nổi bật nội dung Modal
            ctx.fillStyle = 'rgba(15, 23, 42, 0.78)';
            ctx.fillRect(0, 0, w, h);

            const cardMarginX = Math.round(w * 0.06);
            const cardMarginY = Math.round(h * 0.12);
            const maxMediaW = w - cardMarginX * 2;
            const maxMediaH = h - cardMarginY * 2;

            let sourceToDraw: CanvasImageSource | null = null;
            let natW = 16;
            let natH = 9;

            if (hasValidVideo && videoEl && isCleanSourceFn(videoEl)) {
                sourceToDraw = videoEl;
                natW = videoEl.videoWidth;
                natH = videoEl.videoHeight;
            } else if (hasValidImg && imgEl) {
                sourceToDraw = isCleanSourceFn(imgEl) ? imgEl : getCorsSafeImageFn(imgEl.src);
                if (sourceToDraw) {
                    natW = (sourceToDraw as HTMLImageElement).naturalWidth || imgEl.naturalWidth || 16;
                    natH = (sourceToDraw as HTMLImageElement).naturalHeight || imgEl.naturalHeight || 9;
                }
            }

            if (sourceToDraw && natW > 0 && natH > 0) {
                const scale = Math.min(maxMediaW / natW, maxMediaH / natH);
                const drawW = Math.round(natW * scale);
                const drawH = Math.round(natH * scale);
                const drawX = Math.round((w - drawW) / 2);
                const drawY = Math.round((h - drawH) / 2);

                ctx.fillStyle = '#020617';
                ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
                ctx.lineWidth = 2;
                ctx.fillRect(drawX - 4, drawY - 4, drawW + 8, drawH + 8);
                ctx.strokeRect(drawX - 4, drawY - 4, drawW + 8, drawH + 8);
                ctx.drawImage(sourceToDraw, drawX, drawY, drawW, drawH);

                // Vẽ tiêu đề tài liệu bên dưới nếu có
                const titleEl = modalEl.querySelector<HTMLElement>('h1, h2, h3, h4');
                const titleText = titleEl?.textContent?.trim();
                if (titleText) {
                    const fontSize = Math.max(14, Math.round(w * 0.022));
                    ctx.font = `bold ${fontSize}px "Plus Jakarta Sans", system-ui, sans-serif`;
                    ctx.fillStyle = '#ffffff';
                    ctx.textAlign = 'center';
                    ctx.fillText(titleText.slice(0, 70), w / 2, Math.min(h - 24, drawY + drawH + fontSize + 14), maxMediaW);
                }
            }

            ctx.restore();
            break;
        }
    } catch {
        // Bỏ qua lỗi vẽ modal để không ảnh hưởng vòng lặp ghi hình chính
    }
}


/**
 * Vẽ toàn bộ giao diện tương tác AI Agent (HUD, Chat Bubble, Phụ đề, Action Buttons)
 * lên Canvas ghi hình (đặc biệt hữu dụng trên Mobile Canvas Compositor).
 */
export function drawAiInteractionHud(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number
): void {
    if (typeof document === 'undefined') return;

    try {
        const isAiActive = Boolean(
            (typeof window !== 'undefined' && ((window as any).__aiAgentActive || (window as any).isOpenChatBot)) ||
            document.querySelector('.offcanvas-chatbot')
        );
        if (!isAiActive) return;

        ctx.save();

        // 1. Quét tìm và vẽ khung hội thoại (Chat Bubble / Subtitle transcript)
        const messageEls = document.querySelectorAll<HTMLElement>(
            '.agent-chat-bubble, [data-testid="agent-message"], .chat-message, [class*="AgentChatInput"] textarea, [class*="transcript"]'
        );
        let activeText = '';
        for (let i = messageEls.length - 1; i >= 0; i--) {
            const txt = messageEls[i].textContent?.trim() || (messageEls[i] as HTMLTextAreaElement).value?.trim();
            if (txt && txt.length > 1 && !txt.includes('Hỏi trợ lý AI')) {
                activeText = txt;
                break;
            }
        }

        // Lấy phụ đề từ biến toàn cục nếu có
        if (!activeText && typeof window !== 'undefined' && (window as any).__currentAiSubtitle) {
            activeText = String((window as any).__currentAiSubtitle).trim();
        }

        // Vẽ hộp thoại chat nếu có nội dung
        if (activeText) {
            const cardW = Math.min(Math.round(w * 0.88), 640);
            const cardX = Math.round((w - cardW) / 2);
            const fontSize = Math.max(13, Math.round(w * 0.022));
            const lineHeight = Math.round(fontSize * 1.45);
            ctx.font = `500 ${fontSize}px "Plus Jakarta Sans", system-ui, sans-serif`;

            // Wrap text thành nhiều dòng (tối đa 4 dòng)
            const words = activeText.split(' ');
            const lines: string[] = [];
            let currentLine = '';
            for (const word of words) {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                const metrics = ctx.measureText(testLine);
                if (metrics.width > cardW - 36 && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                    if (lines.length >= 4) break;
                } else {
                    currentLine = testLine;
                }
            }
            if (currentLine && lines.length < 4) lines.push(currentLine);

            const cardH = lines.length * lineHeight + 28;
            const cardY = Math.round(h * 0.78) - cardH;

            // Nền glassmorphism cho chat card
            ctx.fillStyle = 'rgba(15, 23, 42, 0.82)';
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
            ctx.lineWidth = 1.5;
            
            // Vẽ rounded rect
            const radius = 16;
            ctx.beginPath();
            ctx.moveTo(cardX + radius, cardY);
            ctx.lineTo(cardX + cardW - radius, cardY);
            ctx.quadraticCurveTo(cardX + cardW, cardY, cardX + cardW, cardY + radius);
            ctx.lineTo(cardX + cardW, cardY + cardH - radius);
            ctx.quadraticCurveTo(cardX + cardW, cardY + cardH, cardX + cardW - radius, cardY + cardH);
            ctx.lineTo(cardX + radius, cardY + cardH);
            ctx.quadraticCurveTo(cardX, cardY + cardH, cardX, cardY + cardH - radius);
            ctx.lineTo(cardX, cardY + radius);
            ctx.quadraticCurveTo(cardX, cardY, cardX + radius, cardY);
            ctx.closePath();
            ctx.fill();
            ctx.stroke();

            // Vẽ text
            ctx.fillStyle = '#f8fafc';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            let textY = cardY + 22;
            for (const line of lines) {
                ctx.fillText(line, cardX + 18, textY);
                textY += lineHeight;
            }
        }

        // 2. Vẽ cụm Action Buttons tại đáy màn hình (Bottom Action Bar)
        const btnRadius = Math.max(18, Math.round(w * 0.032));
        const btnDiameter = btnRadius * 2;
        const barY = Math.round(h - btnRadius * 2.2);
        const centerX = Math.round(w / 2);
        const spacing = Math.round(btnDiameter * 1.35);

        // Nút 1: Call / Play / Pause
        const callBtnX = centerX - spacing;
        ctx.beginPath();
        ctx.arc(callBtnX, barY, btnRadius, 0, Math.PI * 2);
        ctx.fillStyle = '#52c41a'; // Green call
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.stroke();
        // Icon Phone
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${Math.round(btnRadius * 0.9)}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('📞', callBtnX, barY);

        // Nút 2: Record Button (Pulsing Red)
        const recBtnX = centerX;
        ctx.beginPath();
        ctx.arc(recBtnX, barY, btnRadius, 0, Math.PI * 2);
        ctx.fillStyle = '#ef4444'; // Red
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();
        // Inner white dot
        ctx.beginPath();
        ctx.arc(recBtnX, barY, Math.round(btnRadius * 0.38), 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();

        // Nút 3: Close Button
        const closeBtnX = centerX + spacing;
        ctx.beginPath();
        ctx.arc(closeBtnX, barY, btnRadius, 0, Math.PI * 2);
        ctx.fillStyle = '#dc2626'; // Red close
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${Math.round(btnRadius * 0.9)}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('✕', closeBtnX, barY);

        // 3. Header badge ở góc trên (Status Pill)
        const headerW = Math.round(w * 0.44);
        const headerH = Math.round(h * 0.038);
        const headerX = Math.round((w - headerW) / 2);
        const headerY = Math.round(h * 0.03);

        ctx.fillStyle = 'rgba(15, 23, 42, 0.65)';
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        const pillR = headerH / 2;
        ctx.moveTo(headerX + pillR, headerY);
        ctx.lineTo(headerX + headerW - pillR, headerY);
        ctx.arc(headerX + headerW - pillR, headerY + pillR, pillR, -Math.PI / 2, Math.PI / 2);
        ctx.lineTo(headerX + pillR, headerY + headerH);
        ctx.arc(headerX + pillR, headerY + pillR, pillR, Math.PI / 2, -Math.PI / 2);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#38bdf8';
        ctx.font = `600 ${Math.max(11, Math.round(headerH * 0.42))}px "Plus Jakarta Sans", system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('✨ Trợ lý AI Tương tác 360', headerX + headerW / 2, headerY + headerH / 2);

        ctx.restore();
    } catch {
        // Bỏ qua lỗi render HUD để không làm ngắt vòng lặp chính
    }
}
